import { ImageIcon, Trash2 } from 'lucide-react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import type { SettingsFormReturn } from '../useSettingsForm';

export function BackgroundImageSection({ form }: { form: SettingsFormReturn }) {
  const { backgroundImage, backgroundImagePreview, backgroundImageInputRef, handleBackgroundImageChange, handleRemoveBackground } = form;
  const hasBackground = backgroundImagePreview || backgroundImage;

  const handlePickImage = () => backgroundImageInputRef.current?.click();

  return (
    <Box bg="white" borderRadius="2xl" p="6" boxShadow="0 8px 24px rgba(0,0,0,0.12)">
      <Text as="h3" fontSize="lg" fontWeight="semibold" color="volyx.primary" mb="4">
        Background Image
      </Text>
      <Box
        position="relative"
        w="full"
        h={{ base: '48', lg: '56' }}
        borderRadius="xl"
        bg="volyx.lightGray"
        overflow="hidden"
        boxShadow="0 1px 3px rgba(0,0,0,0.08)"
        transition="all 0.2s"
        _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
      >
        <chakra.button
          type="button"
          display="flex"
          w="full"
          h="full"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          border="none"
          bg="transparent"
          outline="none"
          _focus={{ outline: 'none' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          transition="transform 0.3s"
          _hover={{ transform: 'scale(1.02)' }}
          onClick={handlePickImage}
        >
          {hasBackground ? (
            <chakra.img
              src={backgroundImagePreview || backgroundImage || ''}
              alt="Background"
              w="full"
              h="full"
              objectFit="cover"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                (e.target as HTMLImageElement).src = '/volbg.jpg';
              }}
            />
          ) : (
            <Text as="span" color="volyx.textSecondary" fontWeight="medium">
              Upload Image
            </Text>
          )}
        </chakra.button>
        {hasBackground && (
          <Flex position="absolute" bottom="3" right="3" gap="2">
            <chakra.button
              type="button"
              onClick={handlePickImage}
              display="flex"
              w="10"
              h="10"
              bg="white"
              borderRadius="full"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 4px 12px rgba(0,0,0,0.08)"
              _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'scale(1.1)' }}
              _active={{ transform: 'scale(0.95)' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              transition="all 0.3s"
              border="0"
              outline="none"
              _focus={{ outline: 'none' }}
              cursor="pointer"
              color="volyx.action"
              aria-label="Change image"
            >
              <ImageIcon size={22} />
            </chakra.button>
            <chakra.button
              type="button"
              onClick={handleRemoveBackground}
              display="flex"
              w="10"
              h="10"
              bg="white"
              borderRadius="full"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 4px 12px rgba(0,0,0,0.08)"
              _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'scale(1.1)' }}
              _active={{ transform: 'scale(0.95)' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              transition="all 0.3s"
              border="0"
              outline="none"
              _focus={{ outline: 'none' }}
              cursor="pointer"
              color="volyx.error"
              aria-label="Remove image"
            >
              <Trash2 size={20} />
            </chakra.button>
          </Flex>
        )}
        <chakra.input
          ref={backgroundImageInputRef}
          id="background-image-input"
          name="background-image"
          type="file"
          accept="image/*"
          display="none"
          onChange={handleBackgroundImageChange}
        />
      </Box>
    </Box>
  );
}
